import React, { useEffect, useState } from "react";
import { useLazyLoadQuery } from "react-relay";
import { Box, Paper, Typography, styled } from "@mui/material";
import { ParentSize } from "@visx/responsive";
import ControlsBar from "../../components/common/ControlsBar";
import SelectControl from "../../components/common/SelectControl";
import MultiSelect from "../../components/common/MultiSelect";
import KeyValueTable from "../../components/common/KeyValueTable";
import MfdChart, {
  getSeriesNames,
  mfdChartQuery,
  MfdTableMeta,
  parseSeries,
  Series,
  SeriesNames,
} from "../../components/common/MfdChart";
import { displayNames } from "../../constants/mfdSeries";
import { MfdChartQuery } from "../../components/common/__generated__/MfdChartQuery.graphql";
import { useSearchParams } from "react-router-dom";

interface InversionSolutionMfdTabProps {
  mfdTableMeta: MfdTableMeta;
  nodeMeta?:
    | readonly (
        | {
            readonly k: string | null | undefined;
            readonly v: string | null | undefined;
          }
        | null
        | undefined
      )[]
    | null
    | undefined;
}

const Container = styled(Paper)(({ theme }) => ({
  flexGrow: 1,
  backgroundColor: theme.palette.background.paper,
  display: "flex",
  padding: 20,
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "left",
}));

const InversionSolutionMfdTab: React.FC<InversionSolutionMfdTabProps> = ({
  mfdTableMeta,
  nodeMeta: meta,
}: InversionSolutionMfdTabProps) => {
  const { id, isV2, subduction } = mfdTableMeta;
  const [searchParams] = useSearchParams();
  const data = useLazyLoadQuery<MfdChartQuery>(mfdChartQuery, { id });
  const rows = data?.node?.rows;

  const [regionNames, setRegionNames] = useState<string[]>();
  const [seriesNames, setSeriesNames] = useState<SeriesNames>();
  const [region, setRegion] = useState<string>("");
  const [options, setOptions] = useState<string[]>([]);
  const [selectedSeries, setSelectedSeries] = useState<string[]>([]);
  const [series, setSeries] = useState<Series[]>([]);

  // graphQL query is successful, set up seriesNames (all available series names)
  useEffect(() => {
    if (rows) {
      const seriesNames = getSeriesNames(rows, subduction, isV2);
      setSeriesNames(seriesNames);
      setRegionNames(Object.keys(seriesNames).filter((key) => seriesNames[key as keyof SeriesNames].length > 0));
      setRegion(isV2 ? "Both" : "preV2");
    }
  }, [rows]);

  // region has been changed (due to graphQL query or SelectControl)
  // set up available options
  useEffect(() => {
    const options = (isV2 ? seriesNames?.[region as keyof typeof seriesNames] : seriesNames?.preV2) ?? [];
    setOptions(options);
    setSelectedSeries(options);
  }, [region]);

  // series selection has changed, filter data accordingly
  useEffect(() => {
    setSeries(selectedSeries?.map((seriesName) => parseSeries(rows, seriesName)));
  }, [selectedSeries]);

  return (
    <Container>
      <Typography variant="h6" gutterBottom>
        Solution Target vs final Magnitude Frequency distribution
      </Typography>
      {isV2 && (
        <ControlsBar>
          <SelectControl name="Region" options={regionNames ?? [""]} setSelection={setRegion} selection={region} />
          {options?.length ? (
            <MultiSelect
              name="MFD Curves"
              options={options}
              optionLabels={(option) => displayNames[option as keyof typeof displayNames] || option}
              selected={selectedSeries}
              setOptions={setSelectedSeries}
            />
          ) : (
            "No Data"
          )}
        </ControlsBar>
      )}
      <ParentSize>
        {(parent) => (
          <Box style={{ border: "1px solid", width: "fit-content", position: "relative" }}>
            <MfdChart series={series} mfdTableMeta={mfdTableMeta} width={Math.min(800, parent.width)} />
          </Box>
        )}
      </ParentSize>
      <KeyValueTable header="Meta" data={meta} />
      {searchParams?.get("debug") && (
        <>
          <Box style={{ border: "1px solid", width: "fit-content", position: "relative" }}>
            <MfdChart series={series} mfdTableMeta={mfdTableMeta} width={300} />
          </Box>
          <Box style={{ border: "1px solid", width: "fit-content", position: "relative" }}>
            <MfdChart series={series} mfdTableMeta={mfdTableMeta} width={350} />
          </Box>
          <Box style={{ border: "1px solid", width: "fit-content", position: "relative" }}>
            <MfdChart series={series} mfdTableMeta={mfdTableMeta} width={400} />
          </Box>
          <Box style={{ border: "1px solid", width: "fit-content", position: "relative" }}>
            <MfdChart series={series} mfdTableMeta={mfdTableMeta} width={500} />
          </Box>
          <Box style={{ border: "1px solid", width: "fit-content", position: "relative" }}>
            <MfdChart series={series} mfdTableMeta={mfdTableMeta} width={600} />
          </Box>
          <Box style={{ border: "1px solid", width: "fit-content", position: "relative" }}>
            <MfdChart series={series} mfdTableMeta={mfdTableMeta} width={700} />
          </Box>
        </>
      )}
    </Container>
  );
};

export default InversionSolutionMfdTab;
